import { assetStrategyDocument } from '../graphql/assetStrategies';
import React, { useState, useEffect, useRef } from 'react';
import BigNumber from 'bignumber.js';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_INVALIDATION_STRATEGY_TEMPLATES } from '../graphql/queries/strategyTemplates';
import { 
  APPLY_INVALIDATION_STRATEGY, 
  REMOVE_INVALIDATION_STRATEGY,
  UPDATE_INVALIDATION_LEVEL
} from '../graphql/mutations/strategyTemplates';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { formatPrice } from '../utils/formatters';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import toast from 'react-hot-toast';

const CUSTOM_STRATEGY = '__custom__';

const AssignInvalidationModal = ({ 
  assetKind = 'crypto',
  portfolioId, 
  tokenId, 
  tokenName,
  currentLevel,
  averageBuyPrice,
  onClose 
}) => {
  const { currentPortfolio } = usePortfolioContext();
  const currency = currentPortfolio?.currency || 'USD';
  
  // Default to custom strategy, will be updated if editing with a linked template
  const [selectedStrategyId, setSelectedStrategyId] = useState(CUSTOM_STRATEGY);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  
  // Custom settings - default values for new strategy
  const [priceBasedEnabled, setPriceBasedEnabled] = useState(true);
  const [priceDropPercentage, setPriceDropPercentage] = useState('30');
  const [timeBasedEnabled, setTimeBasedEnabled] = useState(false);
  const [timeDurationDays, setTimeDurationDays] = useState(90);
  
  const [saving, setSaving] = useState(false);
  
  // Track template values to detect user modifications
  const templateValuesRef = useRef(null);
  
  // Ref for save handler to avoid stale closure in keyboard event
  const handleSaveRef = useRef(null);

  // Load templates
  const { data: templatesData, loading: templatesLoading } = useQuery(GET_INVALIDATION_STRATEGY_TEMPLATES);
  const templates = templatesData?.invalidationStrategyTemplates || [];
  const hasTemplates = templates.length > 0;

  // Mutations
  const [applyStrategy] = useMutation(assetStrategyDocument(APPLY_INVALIDATION_STRATEGY, assetKind));
  const [removeStrategy] = useMutation(assetStrategyDocument(REMOVE_INVALIDATION_STRATEGY, assetKind));
  const [updateLevel] = useMutation(assetStrategyDocument(UPDATE_INVALIDATION_LEVEL, assetKind));

  // Keyboard handlers: ESC to close, Enter to submit (but not if confirmation modal is open)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showRemoveConfirm) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        // Use ref to get latest handler
        if (handleSaveRef.current) {
          handleSaveRef.current();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, showRemoveConfirm]);

  // Initialize from current level if editing
  useEffect(() => {
    if (currentLevel) {
      const hasPriceCondition = !!currentLevel.triggerPrice;
      const hasTimeCondition = !!currentLevel.timeDurationDays;
      
      setPriceBasedEnabled(hasPriceCondition);
      setTimeBasedEnabled(hasTimeCondition);
      setTimeDurationDays(currentLevel.timeDurationDays || 90);
      
      // Calculate percentage from trigger price if available
      let calculatedPct = '30';
      if (currentLevel.triggerPrice && averageBuyPrice) {
        const avgPrice = new BigNumber(averageBuyPrice);
        const triggerPrice = new BigNumber(currentLevel.triggerPrice);
        if (avgPrice.gt(0)) {
          calculatedPct = avgPrice.minus(triggerPrice).div(avgPrice).times(100).decimalPlaces(8).toFixed();
          setPriceDropPercentage(calculatedPct);
        }
      }
      
      // If there's a linked template, select it and store its values for comparison
      if (currentLevel.templateId) {
        setSelectedStrategyId(currentLevel.templateId);
        // Store these values to detect modifications later
        templateValuesRef.current = {
          priceBasedEnabled: hasPriceCondition,
          priceDropPercentage: calculatedPct,
          timeBasedEnabled: hasTimeCondition,
          timeDurationDays: currentLevel.timeDurationDays || 90
        };
      } else {
        setSelectedStrategyId(CUSTOM_STRATEGY);
        templateValuesRef.current = null;
      }
    }
  }, [currentLevel, averageBuyPrice]);

  // When a template is selected, populate the form with its values
  const handleStrategySelect = (strategyId) => {
    setSelectedStrategyId(strategyId);
    
    if (strategyId !== CUSTOM_STRATEGY) {
      const template = templates.find(t => t.id === strategyId);
      if (template) {
        setPriceBasedEnabled(template.priceBasedEnabled);
        setPriceDropPercentage(template.priceDropPercentage?.toString() || '30');
        setTimeBasedEnabled(template.timeBasedEnabled);
        setTimeDurationDays(template.timeDurationDays || 90);
        
        // Store the template values so we can detect modifications
        templateValuesRef.current = {
          priceBasedEnabled: template.priceBasedEnabled,
          priceDropPercentage: template.priceDropPercentage?.toString() || '30',
          timeBasedEnabled: template.timeBasedEnabled,
          timeDurationDays: template.timeDurationDays || 90
        };
      }
    } else {
      // Switched to custom - clear template reference
      templateValuesRef.current = null;
    }
  };

  // Check if form values have been modified from the selected template
  const checkIfModified = () => {
    if (!templateValuesRef.current) return false;
    
    return (
      priceBasedEnabled !== templateValuesRef.current.priceBasedEnabled ||
      priceDropPercentage !== templateValuesRef.current.priceDropPercentage ||
      timeBasedEnabled !== templateValuesRef.current.timeBasedEnabled ||
      timeDurationDays !== templateValuesRef.current.timeDurationDays
    );
  };

  // When form values change, switch to custom if a template was selected and values were modified
  useEffect(() => {
    if (selectedStrategyId !== CUSTOM_STRATEGY && checkIfModified()) {
      setSelectedStrategyId(CUSTOM_STRATEGY);
      templateValuesRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBasedEnabled, priceDropPercentage, timeBasedEnabled, timeDurationDays]);

  const handleSave = async () => {
    if (!priceBasedEnabled && !timeBasedEnabled) {
      toast.error('Enable at least one condition');
      return;
    }
    if (saving) return;

    const drop = new BigNumber(priceDropPercentage);
    if (priceBasedEnabled && (!drop.isFinite() || drop.lte(0) || drop.gte(100))) {
      toast.error('Enter a price drop greater than 0% and below 100%');
      return;
    }
    if (priceBasedEnabled && !new BigNumber(averageBuyPrice || '0').gt(0)) {
      toast.error('A positive average buy price is required for a price alert');
      return;
    }
    if (timeBasedEnabled && (!Number.isInteger(timeDurationDays) || timeDurationDays < 1)) {
      toast.error('Enter a positive number of days');
      return;
    }

    setSaving(true);
    try {
      // If a saved strategy is selected (not custom), apply the template
      if (selectedStrategyId !== CUSTOM_STRATEGY) {
        const { data } = await applyStrategy({
          variables: { templateId: selectedStrategyId, portfolioId, tokenId }
        });
        if (data?.applyInvalidationStrategy?.success) {
          toast.success('Invalidation strategy applied');
          onClose();
        } else {
          toast.error(data?.applyInvalidationStrategy?.error || 'Failed to apply strategy');
        }
      } else {
        // Custom strategy - update level values (detaches from any saved strategy)
        const input = {
          triggerPrice: priceBasedEnabled && averageBuyPrice 
            ? new BigNumber(averageBuyPrice).times(new BigNumber(1).minus(drop.div(100))).toFixed()
            : null,
          timeDurationDays: timeBasedEnabled ? timeDurationDays : null
        };

        const { data } = await updateLevel({
          variables: { portfolioId, tokenId, input }
        });
        if (data?.updateInvalidationLevel?.success) {
          toast.success('Invalidation strategy updated');
          onClose();
        } else {
          toast.error(data?.updateInvalidationLevel?.error || 'Failed to update');
        }
      }
    } catch (err) {
      toast.error('Failed to save strategy');
    } finally {
      setSaving(false);
    }
  };
  
  // Keep ref updated with latest handleSave for keyboard handler
  useEffect(() => {
    handleSaveRef.current = handleSave;
  });

  const handleRemove = async () => {
    setSaving(true);
    try {
      const { data } = await removeStrategy({
        variables: { portfolioId, tokenId }
      });
      if (data?.removeInvalidationStrategy?.success) {
        toast.success('Invalidation strategy removed');
        onClose();
      } else {
        toast.error(data?.removeInvalidationStrategy?.error || 'Failed to remove');
      }
    } catch (err) {
      toast.error('Failed to remove strategy');
    } finally {
      setSaving(false);
      setShowRemoveConfirm(false);
    }
  };

  const isFormValid = priceBasedEnabled || timeBasedEnabled;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {currentLevel ? 'Edit' : 'Set'} Invalidation Strategy
              {tokenName && <span className="text-gray-500 font-normal ml-2">for {tokenName}</span>}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Strategy dropdown */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Strategy
              </label>
              <Link 
                to="/strategies?tab=invalidation" 
                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Edit strategies →
              </Link>
            </div>
            {templatesLoading ? (
              <div className="text-center py-2 text-gray-500">Loading strategies...</div>
            ) : (
              <select
                value={selectedStrategyId}
                onChange={(e) => handleStrategySelect(e.target.value)}
                disabled={!hasTemplates}
                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  !hasTemplates ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value={CUSTOM_STRATEGY}>Custom strategy</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                    {template.priceBasedEnabled && ` (>${template.priceDropPercentage}% drop`}
                    {template.priceBasedEnabled && template.timeBasedEnabled && ', '}
                    {!template.priceBasedEnabled && template.timeBasedEnabled && ' ('}
                    {template.timeBasedEnabled && `>${template.timeDurationDays}d)`}
                    {template.priceBasedEnabled && !template.timeBasedEnabled && ')'}
                  </option>
                ))}
              </select>
            )}
            {!hasTemplates && !templatesLoading && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                No saved strategies available. Configure custom settings below.
              </p>
            )}
          </div>

          {/* Form fields - always shown */}
          <div className="space-y-6">
            {/* Price-based */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={priceBasedEnabled}
                  onChange={(e) => setPriceBasedEnabled(e.target.checked)}
                  className="mr-3 w-5 h-5"
                />
                <span className="font-medium text-gray-900 dark:text-white">Price Drop Alert</span>
              </label>
              {priceBasedEnabled && (
                <div className="ml-8">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Alert when price drops more than X% from average buy price
                    {averageBuyPrice && (
                      <span className="block mt-1 text-xs">
                        Current avg: {formatPrice(averageBuyPrice, currency)} → 
                        Trigger at: {formatPrice(parseFloat(averageBuyPrice) * (1 - parseFloat(priceDropPercentage) / 100), currency)}
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 shrink-0">
                      <input
                        type="number"
                        value={priceDropPercentage}
                        onChange={(e) => setPriceDropPercentage(e.target.value)}
                        className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        min="5"
                        max="90"
                      />
                      <span className="text-gray-600 dark:text-gray-400">%</span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="90"
                      value={priceDropPercentage}
                      onChange={(e) => setPriceDropPercentage(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Time-based */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={timeBasedEnabled}
                  onChange={(e) => setTimeBasedEnabled(e.target.checked)}
                  className="mr-3 w-5 h-5"
                />
                <span className="font-medium text-gray-900 dark:text-white">Time Limit Alert</span>
              </label>
              {timeBasedEnabled && (
                <div className="ml-8">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Alert when position is held longer than X days
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 shrink-0">
                      <input
                        type="number"
                        value={timeDurationDays}
                        onChange={(e) => setTimeDurationDays(parseInt(e.target.value) || 0)}
                        className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        min="1"
                      />
                      <span className="text-gray-600 dark:text-gray-400">days</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {[30, 90, 180, 365].map(days => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => setTimeDurationDays(days)}
                          className={`px-3 py-1 text-sm rounded ${
                            timeDurationDays === days
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {days < 365 ? `${days}d` : '1y'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {priceBasedEnabled && timeBasedEnabled && (
              <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
                Alert triggers when <strong>either</strong> condition is met (OR logic)
              </div>
            )}

            {/* Buttons */}
            <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                {currentLevel && (
                  <button
                    type="button"
                    onClick={() => setShowRemoveConfirm(true)}
                    className="px-4 py-2 text-red-600 hover:text-red-800 dark:text-red-400"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!isFormValid || saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showRemoveConfirm && (
        <ConfirmDeleteModal
          title="Remove Invalidation Strategy"
          message="Are you sure you want to remove the invalidation strategy from this position?"
          onConfirm={handleRemove}
          onCancel={() => setShowRemoveConfirm(false)}
        />
      )}
    </div>
  );
};

export default AssignInvalidationModal;
